/**
 * Global SFX manager.
 * Handles one-shot UI sound effects (button clicks, choice selection)
 * from /assets/audio/sfx/.
 */

import { Howl, Howler } from 'howler'
import { assetUrl } from '../../utils/pathResolver'
import { unlockAudioContext } from './unlock-audio-context'

const SFX_BASE = '/assets/audio/sfx'

export interface UiSfxConfig {
  buttonClick: string
  choice: string
}

export class SfxManager {
  private buttonClick: Howl
  private choice: Howl
  private buttonClickUrl: string
  private choiceUrl: string
  private volume = 0.7
  private clickHandler: ((e: MouseEvent) => void) | null = null

  constructor(config: UiSfxConfig) {
    this.buttonClickUrl = assetUrl(`${SFX_BASE}/${config.buttonClick}`)
    this.choiceUrl = assetUrl(`${SFX_BASE}/${config.choice}`)

    this.buttonClick = new Howl({
      src: [this.buttonClickUrl],
      volume: this.volume,
      preload: false,
      html5: false,
    })

    this.choice = new Howl({
      src: [this.choiceUrl],
      volume: this.volume,
      preload: false,
      html5: false,
    })
  }

  init(): void {
    this.clickHandler = (e: MouseEvent) => {
      const target = e.target as HTMLElement
      const button = target.closest('button, [role="button"]')
      if (!button) return

      const isChoice = button.matches('.choice-button')
      const isSpeechTrigger =
        !!target.closest('.action-point') ||
        !!target.closest('.bubble-image') ||
        !!target.closest('.homework-overlay__paper')

      if (isChoice) {
        this.playChoice()
      } else if (!isSpeechTrigger) {
        this.playButtonClick()
      }
    }

    document.addEventListener('click', this.clickHandler, { capture: true })
  }

  setVolume(v: number): void {
    this.volume = Math.max(0, Math.min(1, v))
  }

  private loadLowPriority(howl: Howl, url: string): void {
    if (howl.state() !== 'unloaded') return
    fetch(url, { priority: 'low' } as RequestInit)
      .catch(() => {})
      .finally(() => { if (howl.state() === 'unloaded') howl.load() })
  }

  private playHowl(howl: Howl, url: string): void {
    if (howl.state() === 'unloaded') { this.loadLowPriority(howl, url); return }
    if (howl.state() !== 'loaded') return

    const doPlay = () => {
      const id = howl.play() as unknown as number
      howl.volume(this.volume, id)
    }

    howl.once('playerror', () => {
      unlockAudioContext()
      const ctx = Howler.ctx as AudioContext | undefined
      if (ctx && ctx.state === 'suspended') {
        ctx.resume().then(() => { howl.once('unlock', doPlay) })
      } else {
        howl.once('unlock', doPlay)
      }
    })

    doPlay()
  }

  private playButtonClick(): void {
    this.playHowl(this.buttonClick, this.buttonClickUrl)
  }

  private playChoice(): void {
    this.playHowl(this.choice, this.choiceUrl)
  }

  destroy(): void {
    if (this.clickHandler) {
      document.removeEventListener('click', this.clickHandler, { capture: true })
      this.clickHandler = null
    }
    this.buttonClick.unload()
    this.choice.unload()
  }
}
